import { ReactNode, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useLessonArticleFooter } from "./LessonArticleFooter";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { CodingLessonHeader } from "../layout/CodingLessonHeader";
import { ResumeArea } from "../domain/ResumeArea";
import type { GlossaryEntry } from "../base/MarkdownContent";
import { ChatArea } from "../domain/ChatArea";
import { CodingEditorArea } from "../domain/CodingEditorArea";
import { CursorPointer } from "../base/CursorPointer";
import { Button } from "../base/Button";
import type { CodingLessonBreadcrumbItem } from "../layout/CodingLessonHeader";
import type {
  ContentSection,
  TabItem,
  ResumeTabsVariant,
  SectionNavItem,
} from "../domain/ResumeArea";
import type { Message } from "../domain/ChatArea";
import type { FileTreeItem } from "../domain/CodingEditorArea";

export interface CodingLessonPageProps {
  // Header props
  breadcrumbs: CodingLessonBreadcrumbItem[];
  studyingCount?: number;
  userName?: string;
  userEmail?: string;
  userLevel?: number;
  userRank?: string;
  userTotalXp?: number;
  userXpToNextLevel?: number;
  onMenuClick?: () => void;
  onStudyingClick?: () => void;
  onUserClick?: () => void;
  onSettingsClick?: () => void;
  onFontSizeClick?: () => void;
  onKeymapClick?: () => void;

  /**
   * 帯の右側クラスタの先頭に差し込む面 (ゆめさくコーチのつまみ等)。
   * 状態は呼び出し側が持つ。渡さない画面は今までどおり
   */
  headerAction?: ReactNode;

  // Course navigation props
  course?: {
    title: string;
    description?: string;
    mark?: { language: string; alt: string };
    sections: Array<{
      stepNumber: number;
      title: string;
      lessons: Array<{
        id: string;
        number: string;
        title: string;
        duration: number;
        type: "text" | "code" | "quiz";
        status: "completed" | "current" | "available" | "locked";
        slug?: string;
      }>;
      defaultOpen?: boolean;
    }>;
  };
  courseSlug?: string;
  LinkComponent?: any;
  onLessonClick?: (lessonId: string) => void;

  // Resume Area props
  resumeTabs?: TabItem[];
  resumeSectionNav?: SectionNavItem[];
  resumeSections: ContentSection[];
  /*
    解説の頭 (レッスン名の見出しの直下) に差す枠。解説動画のカードのように、
    どの View でも同じ位置に出したいものを、View ごとに書き写さずに渡す
  */
  resumeLead?: ReactNode;
  defaultActiveResumeTab?: string;
  onResumeTabChange?: (tabId: string) => void;
  onAiHelp?: (code: string) => void;

  // Chat Area props
  chatMessages: Message[];
  chatInitialMessage?: ReactNode;
  chatInputPlaceholder?: string;
  onSendChatMessage?: (message: string) => void;
  chatDisabled?: boolean;
  chatDisabledMessage?: ReactNode;
  chatIsLoading?: boolean;
  onCloseChatArea?: () => void;
  onOpenChatArea?: () => void;
  /** チャットの入力欄のすぐ上に添えるもの (AI チューターのモデルの切り替え) */
  chatInputAccessory?: ReactNode;

  // Coding Editor props
  fileTree: FileTreeItem[];
  /**
   * エディター下部の操作バー（保存・リセット・実行・提出など）を出すか。
   * 読み取り専用のデモでは false にする。既定は出す。
   */
  showEditorActions?: boolean;
  /** 結果欄の見出し。既定は「コードの実行結果」 */
  executionResultLabel?: string;
  /** 操作バーに保存を出すか。既定は通常レッスン向けに true */
  showSaveAction?: boolean;
  /** 操作バーに提出を出すか。既定は通常レッスン向けに true */
  showSubmitAction?: boolean;
  /** 実行ボタンの表示名。既定は「実行する」 */
  runActionLabel?: string;
  /** 左のファイル一覧を出すか。既定は出す */
  showFileSidebar?: boolean;
  /** ファイル一覧の新規作成操作を出すか。既定は出す */
  showFileCreateActions?: boolean;
  openFiles: string[];
  activeFileId?: string;
  /*
    左の解説カラムの一番上に差す枠。**本文と入れ替えるのではなく、上に足す。**
    切り替えのつまみをここに置くので、本文を隠している間も戻す手が残る
  */
  articleTopSlot?: ReactNode;
  /** true の間は本文 (ResumeArea) を描かない。articleTopSlot だけが残る */
  hideArticleBody?: boolean;
  editorContent?: ReactNode;
  executionResult?: ReactNode;
  /** 提出の結果 (「できているか」)。提出の釦のすぐ上に出す */
  submitResult?: ReactNode;
  fontSize?: number;
  onFontSizeChange?: (size: number) => void;
  onFileClick?: (fileId: string) => void;
  onCloseFile?: (fileId: string) => void;
  onCreateFile?: () => void;
  onCreateFolder?: () => void;
  onRunCode?: () => void;
  isRunning?: boolean;
  isSubmitting?: boolean;
  submitDisabled?: boolean;
  /** 実行・提出・リセットとショートカットをまとめて止める。CodingEditorArea へそのまま渡す */
  actionsDisabled?: boolean;
  /** 操作バーに出す一言 */
  actionsNotice?: ReactNode;
  /** 操作バーの左の並び (言語と設定のあと) に足す釦 (手書きメモなど)。CodingEditorArea の toolbarExtra */
  editorToolbarExtra?: ReactNode;
  /** 操作バーに触れた合図 */
  onActionsIntent?: () => void;
  onPreviewCode?: () => void;
  onSaveCode?: () => void;
  onSubmitCode?: () => void;
  onResetCode?: () => void;

  // Language selection props
  availableLanguages?: string[];
  selectedLanguage?: string;
  onLanguageChange?: (language: string) => void;

  // Execution snackbar
  showExecutionSnackbar?: boolean;
  /** 実行した結果、まだ通っていないテストがあるか (CodingEditorArea の executionFailed) */
  executionFailed?: boolean;

  // Layout control
  initialLayout?: "split" | "resume-only" | "editor-only";
  showResumeArea?: boolean;
  showChatArea?: boolean;
  showEditorArea?: boolean;
  onLayoutChange?: (layout: {
    showResumeArea: boolean;
    showChatArea: boolean;
    showEditorArea: boolean;
  }) => void;

  // Bookmark & Notes props
  isBookmarked?: boolean;
  onBookmarkToggle?: () => void;
  onNotesClick?: () => void;
  hasNotes?: boolean;

  // Text selection props
  onTextSelectionQuestion?: (selectedText: string, question: string) => void;

  // Lesson completion status
  isLessonCompleted?: boolean;

  // Solution props
  hasSolution?: boolean;
  onShowSolution?: () => void;

  // Reference links
  referenceLinks?: string[];

  // 用語ハイライト辞書 (Resume Area の markdown セクション内の用語に
  // ホバー説明 + /glossary/:slug への内部リンクを付与する)
  glossary?: GlossaryEntry[];
  /** 説明の面のいちばん下に置く物 (スクールの案内の帯など)。エディターの側には出さない */
  articleFooter?: ReactNode;

  // Additional props
  className?: string;

  /**
   * On narrow screens, stack the lesson content and editor vertically.
   * Kept opt-in so the established three-pane lesson layout is unchanged.
   */
  stackEditorOnMobile?: boolean;
  /**
   * レッスンの頭 (CodingLessonHeader) を出すか。既定は出す。
   * 公開の頁の器 (PublicPageShell) の中に置く頁は、器が頭を持つので出さない
   */
  showHeader?: boolean;
  /** 頭の設定のつまみ (図解の自動再生) を出すか。既定は出す */
  showHeaderSettings?: boolean;
  /** 頭の左の釦で開く引き出し。渡さない画面は今までどおり (コースの一覧) */
  headerMenu?: { title: string; content: ReactNode };
  /** 引き出しの釦の読み上げの名前 */
  headerMenuLabel?: string;
  /** 頭の左端のメニューの釦を出すか。既定は出す */
  showHeaderMenuButton?: boolean;
  /**
   * スマホの縦積みで、器の中ではなく頁 (window) を送る。既定は器の中を送る (レッスンの頁)。
   * 上に頁の頭の帯や題がある公開の頁で使う。器の中を送ると、帯が上に残ったまま下の狭い所だけが動く。
   * 「エディター」の帯は --lesson-sticky-top (頁の頭の高さ) の下に留まる
   */
  mobilePageScroll?: boolean;
  /** 左の説明の札の字を PC でも 15px にする。既定は今のまま */
  largeResumeTabs?: boolean;
  /** 左の説明の札の列の見た目。既定は pill (今のまま)。flush は欄の上端に付く札の列 (技術面接の頁) */
  resumeTabsVariant?: ResumeTabsVariant;
  /** 提出の釦の字と待つ間の字 (CodingEditorArea へそのまま渡す) */
  submitActionLabel?: string;
  submittingLabel?: string;
  /**
   * スマホで書く途中に見返す課題 (要件)。渡すとエディターの上に「課題を見る」を出し、
   * 押すと下から半分の高さの板で開く。コードの上半分は見えたまま
   */
  mobileTaskContent?: ReactNode;
  /**
   * スマホの縦積みで、画面の下に固定する札 (問題 / コード / 解答 / 話し方)。渡すと次のようになる。
   * - value が codeId の札はエディターだけ、ほかの札は説明だけを出す (説明の上の札は出さない)
   * - 説明の下の「コードを書く」と、エディターの上の「課題を見る」は出さない (行き先が札と二重になるため)
   * 渡さない画面は今までどおり。PC は変えない
   */
  mobileBottomTabs?: {
    items: { id: string; label: string }[];
    value: string;
    /** エディターを出す札の id */
    codeId: string;
    onChange: (id: string) => void;
  };
  /**
   * スマホで説明と書く欄を頁全体の 1 本のスクロールに載せ、説明の下に「コードを書く」を出す。
   * コードを書くレッスンの型 (HTML、React、JS、SQL、端末) だけが渡す。ほかの型 (AI、プロンプトなど) は今までの縦積み
   */
  mobileSingleScroll?: boolean;

  /**
   * Give the lesson article more room in the desktop two-pane layout.
   * This is opt-in so regular coding lessons keep their established sizing.
   */
  wideArticleOnDesktop?: boolean;

  /**
   * 説明とエディターの 2 列の左に置く、問題の並びのサイドバー。1024px 以上では 2 列の左に並び、
   * それより狭い幅では何も場所を取らない (置く側が下から出る棚にする)。渡さない画面は今までどおり
   */
  leftSidebar?: ReactNode;
}

/** 左のサイドバーがあるときだけ、2 列との間を横に並べる (1024px 未満は枠を作らず、今までの縦の並びのまま) */
function WithLeftSidebar({ sidebar, children }: { sidebar: ReactNode; children: ReactNode }) {
  if (!sidebar) return <>{children}</>;
  return (
    <div className="contents lg:flex lg:min-h-0 lg:flex-1 lg:gap-1">
      {sidebar}
      {children}
    </div>
  );
}

function withResumeLead(sections: ContentSection[], lead: ReactNode): ContentSection[] {
  if (lead == null || lead === false) return sections;
  const at = sections[0]?.type === "heading" && sections[0].level === 1 ? 1 : 0;
  return [...sections.slice(0, at), { type: "node", content: lead }, ...sections.slice(at)];
}

const MOBILE_VIEWPORT_QUERY = "(max-width: 767px)";

function subscribeMobileViewport(onChange: () => void) {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const media = window.matchMedia(MOBILE_VIEWPORT_QUERY);
  media.addEventListener?.("change", onChange);
  return () => media.removeEventListener?.("change", onChange);
}

function getMobileViewportSnapshot() {
  return typeof window !== "undefined" && Boolean(window.matchMedia) && window.matchMedia(MOBILE_VIEWPORT_QUERY).matches;
}

function subscribeNever() {
  return () => {};
}

function getMobileViewportServerSnapshot() {
  return false;
}

export function CodingLessonPage({
  // Header
  breadcrumbs,
  studyingCount,
  userName,
  userEmail,
  userLevel,
  userRank,
  userTotalXp,
  userXpToNextLevel,
  onMenuClick,
  onStudyingClick,
  onUserClick,
  onSettingsClick,
  onFontSizeClick,
  onKeymapClick,

  // Course navigation
  course,
  courseSlug,
  LinkComponent,
  onLessonClick,

  // Resume Area
  resumeTabs,
  resumeSectionNav,
  resumeSections: resumeSectionsProp,
  resumeLead,
  defaultActiveResumeTab,
  onResumeTabChange,
  onAiHelp,

  // Chat Area
  chatMessages,
  chatInitialMessage,
  chatInputPlaceholder,
  onSendChatMessage,
  chatInputAccessory,
  chatDisabled = false,
  chatDisabledMessage,
  chatIsLoading = false,
  onCloseChatArea,
  articleTopSlot,
  hideArticleBody = false,
  onOpenChatArea,

  // Coding Editor
  fileTree,
  showEditorActions = true,
  executionResultLabel,
  showSaveAction = true,
  showSubmitAction = true,
  runActionLabel,
  showFileSidebar,
  showFileCreateActions = true,
  openFiles,
  activeFileId,
  editorContent,
  executionResult,
  submitResult,
  fontSize,
  onFontSizeChange,
  onFileClick,
  onCloseFile,
  onCreateFile,
  onCreateFolder,
  onRunCode,
  isRunning = false,
  isSubmitting = false,
  submitDisabled = false,
  actionsDisabled,
  actionsNotice,
  editorToolbarExtra,
  onActionsIntent,
  onPreviewCode,
  onSaveCode,
  onSubmitCode,
  onResetCode,

  // Language selection
  availableLanguages,
  selectedLanguage,
  showExecutionSnackbar = false,
  executionFailed = false,
  onLanguageChange,

  // Layout
  initialLayout = "split",
  showResumeArea: showResumeAreaProp = true,
  showChatArea: showChatAreaProp = true,
  showEditorArea: showEditorAreaProp = true,
  onLayoutChange,

  // Bookmark & Notes
  isBookmarked,
  onBookmarkToggle,
  onNotesClick,
  hasNotes,

  // 帯に差し込む面
  headerAction,

  // Text selection
  onTextSelectionQuestion,

  // Lesson completion status
  isLessonCompleted,

  // Solution
  hasSolution,
  onShowSolution,

  // Reference links
  referenceLinks,

  // 用語ハイライト辞書
  glossary,
  articleFooter: articleFooterProp,

  stackEditorOnMobile = false,
  showHeader = true,
  showHeaderSettings = true,
  headerMenu,
  headerMenuLabel,
  showHeaderMenuButton = true,
  mobilePageScroll = false,
  largeResumeTabs = false,
  resumeTabsVariant,
  submitActionLabel,
  submittingLabel,
  mobileTaskContent,
  mobileBottomTabs,
  mobileSingleScroll = false,
  wideArticleOnDesktop = false,
  leftSidebar,

  className = "",
}: CodingLessonPageProps) {
  const resumeSections = useMemo(
    () => withResumeLead(resumeSectionsProp, resumeLead),
    [resumeSectionsProp, resumeLead],
  );
  void initialLayout;

  const mobileChatRef = useRef<HTMLElement>(null);
  const mobileEditorHeadRef = useRef<HTMLDivElement>(null);
  const [mobileTaskOpen, setMobileTaskOpen] = useState(false);
  // 書いて実行か提出できるときだけ「コードを書く」と「エディター」の帯を出す。体験の読むだけの見本には出さない
  const articleFooter = useLessonArticleFooter(articleFooterProp);
  const mobileWritable = mobileSingleScroll && Boolean(onSubmitCode || onRunCode);

  // 最初の描画から縦に積む。useEffect で後から替えると、スマホで説明とエディターが
  // 左右に並んだ姿が 1〜3 秒見えてから縦に直る。サーバー側の描画は積まない形 (false) のまま
  // 縦に積む指定のない頁は購読しない (viewport の変化で再描画させない)
  const isMobileViewport = useSyncExternalStore(
    stackEditorOnMobile ? subscribeMobileViewport : subscribeNever,
    stackEditorOnMobile ? getMobileViewportSnapshot : getMobileViewportServerSnapshot,
    getMobileViewportServerSnapshot,
  );

  const stackOnMobile = stackEditorOnMobile && isMobileViewport;
  const bottomCode = Boolean(mobileBottomTabs) && mobileBottomTabs?.value === mobileBottomTabs?.codeId;
  // サーバーは画面の幅を知らないので、水和までの HTML は左右の 2 列で出る。
  // 縦に積む指定のある頁は、狭い幅だけ CSS で最初から縦に積む (構造は変えない)。
  // JS の判定 (stackOnMobile) と同じ 767px 以下。リテラルで書くのは Tailwind に拾わせるため
  const ssrStack = stackEditorOnMobile
    ? {
        group: mobilePageScroll ? "max-md:!flex-col max-md:gap-1" : "max-md:!flex-col max-md:!overflow-y-auto max-md:gap-1",
        panel: "max-md:!flex-none max-md:!h-full max-md:!w-full max-md:!overflow-hidden max-md:border-r-0",
        handle: "max-md:hidden",
      }
    : { group: "", panel: "", handle: "" };
  const resumePanelSizes = wideArticleOnDesktop
    ? { defaultSize: 60, minSize: 50, maxSize: 68 }
    : { defaultSize: 35, minSize: 35, maxSize: 40 };
  const editorPanelSizes = wideArticleOnDesktop
    ? { defaultSize: 40, minSize: 32, maxSize: 50 }
    : { defaultSize: 45, minSize: 30, maxSize: undefined };

  // Internal layout state
  const [localShowResumeArea, setLocalShowResumeArea] =
    useState(showResumeAreaProp);
  const [localShowChatArea, setLocalShowChatArea] = useState(showChatAreaProp);
  const [localShowEditorArea, setLocalShowEditorArea] =
    useState(showEditorAreaProp);

  const showResumeArea = showResumeAreaProp ?? localShowResumeArea;
  const showChatArea = showChatAreaProp ?? localShowChatArea;
  const showEditorArea = showEditorAreaProp ?? localShowEditorArea;

  useEffect(() => {
    if (!stackOnMobile || !showChatArea) return;
    mobileChatRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [showChatArea, stackOnMobile]);

  const handleLayoutChange = (layout: {
    showResumeArea: boolean;
    showChatArea: boolean;
    showEditorArea: boolean;
  }) => {
    setLocalShowResumeArea(layout.showResumeArea);
    setLocalShowChatArea(layout.showChatArea);
    setLocalShowEditorArea(layout.showEditorArea);
    onLayoutChange?.(layout);
  };

  return (
    <CursorPointer
      className={`flex flex-col h-[calc(100vh-56px)] w-full bg-main overflow-hidden ${
        mobilePageScroll ? "max-md:!h-auto max-md:!overflow-visible" : ""
      } ${className}`}
    >
      {/* Header */}
      {showHeader && (
        <CodingLessonHeader
          breadcrumbs={breadcrumbs}
          studyingCount={studyingCount}
          userName={userName}
          userEmail={userEmail}
          userLevel={userLevel}
          userRank={userRank}
          userTotalXp={userTotalXp}
          userXpToNextLevel={userXpToNextLevel}
          onMenuClick={onMenuClick}
          onStudyingClick={onStudyingClick}
          onUserClick={onUserClick}
          course={course}
          courseSlug={courseSlug}
          LinkComponent={LinkComponent}
          onLessonClick={onLessonClick}
          isBookmarked={isBookmarked}
          onBookmarkToggle={onBookmarkToggle}
          onNotesClick={onNotesClick}
          hasNotes={hasNotes}
          isLessonCompleted={isLessonCompleted}
          headerAction={headerAction}
          showSettings={showHeaderSettings}
          menu={headerMenu}
          menuActionLabel={headerMenuLabel}
          showMenuButton={showHeaderMenuButton}
        />
      )}

      <main
        className={`flex flex-col flex-1 w-full bg-third px-1 pb-2 pt-1 overflow-hidden ${
          mobilePageScroll ? "max-md:!overflow-visible" : ""
        }`}
      >
        {/* Main Content - 3 Column Layout with Resizable Panels */}
        <WithLeftSidebar sidebar={leftSidebar}>
        {stackOnMobile ? (
          /* 1 枚ずつの高さは見えている枠と同じ h-full にする。画面の高さから引いた値だと
             下のレッスン送りの帯の分だけはみ出し、実行と提出の釦が帯の下に隠れていた */
          <div
            data-trial-mobile-layout="stacked"
            className={
              mobilePageScroll ? "flex flex-col gap-1" : "flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto"
            }
          >
            {showResumeArea && (
              /* 説明は枠の中で送らず、頁全体の 1 本のスクロールに載せる。
                 前は説明の枠と外の枠の 2 重のスクロールで、説明を読み終えてもエディターへの道が無かった (2026-10-28 の点検 G2) */
              <article
                data-lesson-mobile-panel="article"
                hidden={bottomCode}
                className={`flex w-full shrink-0 flex-col ${
                  mobileSingleScroll ? "" : "h-full overflow-hidden"
                } ${articleTopSlot ? "rounded-lg bg-main" : ""}`}
              >
                {articleTopSlot}
                {!hideArticleBody && (
                  <ResumeArea
                    tabs={mobileBottomTabs ? [] : resumeTabs}
                    largeTabs={largeResumeTabs}
                    tabsVariant={resumeTabsVariant}
                    sectionNav={resumeSectionNav}
                    defaultActiveTab={defaultActiveResumeTab}
                    sections={resumeSections}
                    showGradient={false}
                    onTabChange={onResumeTabChange}
                    onAiHelp={onAiHelp}
                    onTextSelectionQuestion={onTextSelectionQuestion}
                    referenceLinks={referenceLinks}
                    glossary={glossary}
                    // 高さは中身なり。本文の下の 240px の余白も詰める (下に「コードを書く」が付いてくる)
                    className={mobileSingleScroll ? "!h-auto !overflow-visible [&>div.pb-60]:!pb-4" : ""}
                  />
                )}
                {showEditorArea && mobileWritable && !mobileBottomTabs ? (
                  <div className="sticky bottom-2 z-10 px-3 pb-1 pt-3">
                    <Button
                      variant="primary"
                      size="lg"
                      fullWidth
                      className="!text-[17px]"
                      onClick={() =>
                        mobilePageScroll
                          ? scrollPageTo(mobileEditorHeadRef.current)
                          : scrollStackTo(mobileEditorHeadRef.current)
                      }
                    >
                      コードを書く
                    </Button>
                  </div>
                ) : null}
              </article>
            )}

            {showEditorArea && (
              /* 印は PC の Panel と同じ。動画の「演習を始める」が本文の中のコードの箱と分けて、ここのエディタを探す */
              <section
                data-lesson-panel="editor"
                hidden={Boolean(mobileBottomTabs) && !bottomCode}
                className={
                  mobileSingleScroll
                    ? "flex min-h-full w-full shrink-0 flex-col border-main"
                    : "flex h-full w-full shrink-0 overflow-hidden border-main"
                }
              >
                {mobileWritable ? (
                <div
                  ref={mobileEditorHeadRef}
                  className="sticky top-[var(--lesson-sticky-top,0px)] z-10 mb-1 flex min-h-12 shrink-0 items-center justify-between gap-2 rounded-lg bg-main px-3 py-1.5"
                >
                  {/* 説明の下の「コードを書く」と同じ字を並べない。ここは行き先の名前 */}
                  <span className="text-[17px] font-bold text-main">エディター</span>
                  {mobileTaskContent ? (
                    <Button
                      variant="outline"
                      size="md"
                      className="!text-[17px]"
                      aria-haspopup="dialog"
                      aria-expanded={mobileTaskOpen}
                      onClick={() => setMobileTaskOpen(true)}
                    >
                      課題を見る
                    </Button>
                  ) : null}
                </div>
                ) : null}
                <CodingEditorArea
                  className={mobileSingleScroll ? "min-h-0 flex-1 !h-auto" : ""}
                  flowOnMobile={mobileSingleScroll}
                  compactOnMobile={Boolean(mobileBottomTabs)}
                  fileTree={fileTree}
                  showEditorActions={showEditorActions}
                  showSaveAction={showSaveAction}
                  showSubmitAction={showSubmitAction}
                  runActionLabel={runActionLabel}
                  submitActionLabel={submitActionLabel}
                  submittingLabel={submittingLabel}
                  showFileSidebar={showFileSidebar}
                  showFileCreateActions={showFileCreateActions}
                  openFiles={openFiles}
                  activeFileId={activeFileId}
                  editorContent={editorContent}
                  executionResult={executionResult}
                  submitResult={submitResult}
                  executionResultLabel={executionResultLabel}
                  fontSize={fontSize}
                  onFontSizeChange={onFontSizeChange}
                  onFileClick={onFileClick}
                  onCloseFile={onCloseFile}
                  onCreateFile={onCreateFile}
                  onCreateFolder={onCreateFolder}
                  onRun={onRunCode}
                  isRunning={isRunning}
                  isSubmitting={isSubmitting}
                  submitDisabled={submitDisabled}
                  actionsDisabled={actionsDisabled}
                  actionsNotice={actionsNotice}
                  toolbarExtra={editorToolbarExtra}
                  onActionsIntent={onActionsIntent}
                  onPreview={onPreviewCode}
                  onSave={onSaveCode}
                  onSubmit={onSubmitCode}
                  onReset={onResetCode}
                  onSettingsClick={onSettingsClick}
                  onFontSizeClick={onFontSizeClick}
                  onKeymapClick={onKeymapClick}
                  availableLanguages={availableLanguages}
                  selectedLanguage={selectedLanguage}
                  onLanguageChange={onLanguageChange}
                  showExecutionSnackbar={showExecutionSnackbar}
                  executionFailed={executionFailed}
                  hasSolution={hasSolution}
                  onShowSolution={onShowSolution}
                />
              </section>
            )}

            {/* 説明の下の物は、スマホではエディターと実行結果のさらに下、頁のいちばん下に置く。
                説明とエディターの間に挟むと、課題からコードを書くまでの流れを切る */}
            {articleFooter ? (
              <div data-lesson-mobile-panel="article-footer" className="shrink-0 rounded-lg bg-main px-4 py-4 empty:hidden">
                {articleFooter}
              </div>
            ) : null}

            {showChatArea && (
              <section
                ref={mobileChatRef}
                data-lesson-mobile-panel="chat"
                className="flex h-full w-full shrink-0 flex-col overflow-hidden"
              >
                <ChatArea
                  messages={chatMessages}
                  initialMessage={chatInitialMessage}
                  inputPlaceholder={chatInputPlaceholder}
                  onSendMessage={onSendChatMessage}
                  disabled={chatDisabled}
                  disabledMessage={chatDisabledMessage}
                  isLoading={chatIsLoading}
                  onClose={onCloseChatArea}
                  inputAccessory={chatInputAccessory}
                />
              </section>
            )}
            {mobileTaskContent && mobileTaskOpen ? (
              <MobileTaskSheet onClose={() => setMobileTaskOpen(false)}>
                {mobileTaskContent}
              </MobileTaskSheet>
            ) : null}
            {mobileBottomTabs ? <MobileBottomTabs {...mobileBottomTabs} /> : null}
          </div>
        ) : (
          <PanelGroup
            direction="horizontal"
            data-lesson-desktop-layout={
              wideArticleOnDesktop ? "wide-article" : "standard"
            }
            className={`flex-1 gap-0.5 overflow-hidden ${ssrStack.group}`}
          >
            {/* Left Pane - Resume Area */}
            {showResumeArea && (
              <>
                <Panel
                  {...resumePanelSizes}
                  data-lesson-panel="article"
                  data-lesson-default-size={resumePanelSizes.defaultSize}
                  data-lesson-min-size={resumePanelSizes.minSize}
                  data-lesson-max-size={resumePanelSizes.maxSize}
                  className={`flex flex-col overflow-auto ${ssrStack.panel}`}
                >
                  {/*
                    上に差す枠があるときは、article 自体をカードにする。
                    **ResumeArea が自前で白いカードを描くので、その上に
                    足した枠だけが地の灰色の上に浮いて見えていた。**
                    同じ bg-main で包み、内側の角は overflow-hidden で隠す
                  */}
                  <article
                    className={`flex flex-col flex-1 min-h-0 ${
                      articleTopSlot ? "overflow-hidden rounded-lg bg-main" : ""
                    }`}
                  >
                    {articleTopSlot}
                    {!hideArticleBody && (
                      <ResumeArea
                        tabs={resumeTabs}
                        largeTabs={largeResumeTabs}
                        tabsVariant={resumeTabsVariant}
                        sectionNav={resumeSectionNav}
                        defaultActiveTab={defaultActiveResumeTab}
                        sections={resumeSections}
                        showGradient={false}
                        onTabChange={onResumeTabChange}
                        onAiHelp={onAiHelp}
                        onTextSelectionQuestion={onTextSelectionQuestion}
                        referenceLinks={referenceLinks}
                        glossary={glossary}
                        footer={articleFooter}
                      />
                    )}
                  </article>
                </Panel>
                <PanelResizeHandle className={`w-1 hover:bg-primary transition-colors cursor-col-resize ${ssrStack.handle}`} />
              </>
            )}

            {/* Center Pane - Coding Editor Area */}
            {showEditorArea && (
              <>
                <Panel
                  {...editorPanelSizes}
                  data-lesson-panel="editor"
                  data-lesson-default-size={editorPanelSizes.defaultSize}
                  data-lesson-min-size={editorPanelSizes.minSize}
                  data-lesson-max-size={editorPanelSizes.maxSize}
                  className={`flex overflow-hidden border-r border-main ${ssrStack.panel}`}
                >
                  <CodingEditorArea
                    fileTree={fileTree}
                    showEditorActions={showEditorActions}
                    showSaveAction={showSaveAction}
                    showSubmitAction={showSubmitAction}
                    runActionLabel={runActionLabel}
                  submitActionLabel={submitActionLabel}
                  submittingLabel={submittingLabel}
                    showFileSidebar={showFileSidebar}
                    showFileCreateActions={showFileCreateActions}
                    openFiles={openFiles}
                    activeFileId={activeFileId}
                    editorContent={editorContent}
                    executionResult={executionResult}
                    submitResult={submitResult}
                    executionResultLabel={executionResultLabel}
                    fontSize={fontSize}
                    onFontSizeChange={onFontSizeChange}
                    onFileClick={onFileClick}
                    onCloseFile={onCloseFile}
                    onCreateFile={onCreateFile}
                    onCreateFolder={onCreateFolder}
                    onRun={onRunCode}
                    isRunning={isRunning}
                    isSubmitting={isSubmitting}
                    submitDisabled={submitDisabled}
                  actionsDisabled={actionsDisabled}
                  actionsNotice={actionsNotice}
                  toolbarExtra={editorToolbarExtra}
                  onActionsIntent={onActionsIntent}
                    onPreview={onPreviewCode}
                    onSave={onSaveCode}
                    onSubmit={onSubmitCode}
                    onReset={onResetCode}
                    onSettingsClick={onSettingsClick}
                    onFontSizeClick={onFontSizeClick}
                    onKeymapClick={onKeymapClick}
                    availableLanguages={availableLanguages}
                    selectedLanguage={selectedLanguage}
                    onLanguageChange={onLanguageChange}
                    showExecutionSnackbar={showExecutionSnackbar}
                  executionFailed={executionFailed}
                    hasSolution={hasSolution}
                    onShowSolution={onShowSolution}
                  />
                </Panel>
                {showChatArea && (
                  <PanelResizeHandle className={`w-1  hover:bg-primary transition-colors cursor-col-resize ${ssrStack.handle}`} />
                )}
              </>
            )}

            {/* Right Pane - Chat Area */}
            {showChatArea && (
              <Panel
                defaultSize={30}
                minSize={15}
                maxSize={35}
                className={`flex flex-col overflow-hidden ${ssrStack.panel}`}
              >
                <ChatArea
                  messages={chatMessages}
                  initialMessage={chatInitialMessage}
                  inputPlaceholder={chatInputPlaceholder}
                  onSendMessage={onSendChatMessage}
                  disabled={chatDisabled}
                  disabledMessage={chatDisabledMessage}
                  isLoading={chatIsLoading}
                  onClose={onCloseChatArea}
                  inputAccessory={chatInputAccessory}
                />
              </Panel>
            )}
          </PanelGroup>
        )}
        </WithLeftSidebar>
      </main>

      {/* Floating Chat Button - shown when chat is closed */}
      {!showChatArea && onOpenChatArea && (
        <button
          onClick={onOpenChatArea}
          className="fixed bottom-24 right-8 w-14 h-14 rounded-full bg-primary text-white shadow-lg flex items-center justify-center cursor-pointer hover:brightness-110 transition-all z-50"
          aria-label="AIチャットを開く"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </CursorPointer>
  );
}

/**
 * 縦に積んだ枠の中だけを送って、el を枠の上端に合わせる。
 * scrollIntoView は頁 (window) まで送り、上の帯が隠れて下に白い余りが出た
 */
/** 頁 (window) を送って、el を留まる位置 (sticky の top) に合わせる。mobilePageScroll の頁で使う */
function scrollPageTo(el: HTMLElement | null) {
  if (!el) return;
  const stuckAt = parseFloat(getComputedStyle(el).top) || 0;
  const top = window.scrollY + el.getBoundingClientRect().top - stuckAt;
  window.scrollTo({ top, behavior: prefersReducedMotion() ? "auto" : "smooth" });
}

function scrollStackTo(el: HTMLElement | null) {
  const stack = el?.closest<HTMLElement>('[data-trial-mobile-layout="stacked"]');
  if (!el || !stack) return;
  const top = stack.scrollTop + el.getBoundingClientRect().top - stack.getBoundingClientRect().top;
  stack.scrollTo({ top, behavior: prefersReducedMotion() ? "auto" : "smooth" });
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

/**
 * スマホで書く途中に課題を見返す板。下から半分の高さで開き、上半分のコードは見えたまま。
 * 地を押すか Esc か「閉じる」で閉じる
 */
function MobileTaskSheet({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  const sheetRef = useRef<HTMLDivElement>(null);
  // 親が描き直すたびに onClose が新しくなっても、開いたときの 1 回だけ focus を動かす
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    // 閉じたら開いた釦 (「課題を見る」) へ戻す。キーボードの人が位置を失わない
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    sheetRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !sheetRef.current) return;
      // Tab は板の中だけで回す (aria-modal の板から後ろへ抜けない)
      const items = Array.from(
        sheetRef.current.querySelectorAll<HTMLElement>('button, a[href], [tabindex]:not([tabindex="-1"])'),
      ).filter((el) => !el.hasAttribute("disabled"));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      opener?.focus();
    };
  }, []);
  return (
    <div className="fixed inset-0 z-[70]" data-lesson-task-sheet="">
      <div className="absolute inset-0 bg-black/30" aria-hidden="true" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="課題"
        ref={sheetRef}
        className="absolute inset-x-0 bottom-0 flex max-h-[50svh] flex-col rounded-t-[2px] border-t border-main bg-surface"
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-main px-4 py-2">
          <p className="m-0 text-[17px] font-bold text-main">課題</p>
          <Button variant="ghost" size="md" className="!text-[17px]" onClick={onClose}>
            閉じる
          </Button>
        </div>
        <div className="min-h-0 overflow-y-auto px-4 pb-6 pt-3 text-[17px] leading-relaxed text-main [word-break:auto-phrase]">
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * スマホの下に固定する札。親指で届く所に、頁の行き先を並べる。
 * 札の高さは 56px (指で押す面は 44px 以上)。下の余白は端末のホームバーの分。
 * 頁の下に札の高さ分を空けるのは呼ぶ側 (いちばん下の物が札に隠れないように)
 */
function MobileBottomTabs({
  items,
  value,
  onChange,
}: {
  items: { id: string; label: string }[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <>
      <nav
        aria-label="頁の行き先"
        data-mobile-bottom-tabs=""
        className="fixed inset-x-0 bottom-0 z-40 grid border-t border-main bg-surface pb-[env(safe-area-inset-bottom)]"
        style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
      >
        {items.map((item) => {
          const selected = item.id === value;
          return (
            <Button
              key={item.id}
              variant="ghost"
              size="lg"
              aria-current={selected ? "page" : undefined}
              onClick={() => onChange(item.id)}
              className={`!min-h-14 !rounded-none !px-1 !text-[17px] border-t-[3px] focus:!shadow-none focus-visible:!shadow-[inset_0_0_0_2px_var(--color-border-primary)] transition-colors duration-150 ${
                selected ? "!border-t-primary bg-second !text-main" : "!border-t-transparent !text-sub"
              }`}
            >
              {item.label}
            </Button>
          );
        })}
      </nav>
    </>
  );
}
